import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTeam } from './contexts/TeamContext';
import { useAuth } from './contexts/AuthContext';
import { apiFetch } from './utils/httpClient';
import { Modal } from './components/Modal';
import { TeamSelector } from './components/TeamSelector';
import { ThemeToggleButton } from './components/ThemeToggleButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faHome, 
  faChartLine, 
  faChartBar, 
  faCog, 
  faSlidersH,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';

const BackgroundGlow = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  filter: blur(120px);
  z-index: 0;
  transition: opacity 0.3s var(--transition-smooth);
  
  [data-theme='dark'] & {
    background: radial-gradient(circle at 25% 10%, rgba(255, 77, 77, 0.3), transparent 55%),
                radial-gradient(circle at 75% 0%, rgba(232, 19, 19, 0.25), transparent 60%);
    opacity: 0.75;
  }
  
  [data-theme='light'] & {
    background: radial-gradient(circle at 25% 10%, rgba(255, 77, 77, 0.15), transparent 55%),
                radial-gradient(circle at 75% 0%, rgba(232, 19, 19, 0.12), transparent 60%);
    opacity: 0.5;
  }
`;

const LayoutContainer = styled.div`
  min-height: 100vh;
  width: 100vw;
  max-width: 100vw;
  background: var(--color-background);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
  overflow-x: hidden;
  color: var(--color-text-primary);
  transition: background var(--transition-color-fast), color var(--transition-color-fast);
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-4) clamp(var(--spacing-4), 4vw, var(--spacing-10));
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-soft);
  flex-shrink: 0;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  transition: background 0.3s var(--transition-smooth), border-color 0.3s var(--transition-smooth);

  &::after {
    content: '';
    position: absolute;
    inset: auto var(--spacing-6) -0.125rem;
    height: 0.125rem;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(255, 77, 77, 0.6), transparent);
  }
  
  @media (max-width: 64rem) { /* --breakpoint-desktop */
    padding: var(--spacing-4) var(--spacing-8);
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: clamp(var(--spacing-3), 2vw, var(--spacing-4)) var(--spacing-6);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3) var(--spacing-4);
  }
`;


const MatchupDisplay = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  margin-top: 0.25rem;
`;

const IconButton = styled.button`
  width: 2.75rem; /* 44px - accessibility standard */
  height: 2.75rem; /* 44px - accessibility standard */
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s var(--transition-smooth), box-shadow 0.25s var(--transition-smooth), border-color 0.25s var(--transition-smooth);
  font-size: var(--font-size-xl);
  min-height: 2.75rem; /* 44px - accessibility standard */
  min-width: 2.75rem; /* 44px - accessibility standard */
  box-shadow: var(--shadow-inset);

  &:hover {
    transform: translateY(-0.125rem);
    border-color: var(--color-border-strong);
    box-shadow: 0 0 0 0.25rem rgba(255, 43, 43, 0.12), var(--shadow-accent);
  }

  &:active {
    transform: scale(0.96);
  }

  @media (max-width: 30rem) { /* --breakpoint-mobile */
    width: 2.375rem; /* 38px */
    height: 2.375rem; /* 38px */
    font-size: var(--font-size-base);
  }
`;

const HamburgerMenu = styled(IconButton)`
  font-size: 1.4rem;
  line-height: 1;
`;

const ConfigButton = styled(IconButton)`
  cursor: pointer;
  font-size: 1.3rem;
  position: relative;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
`;

const HeaderLogo = styled.img`
  cursor: pointer;
  width: 2.625rem; /* 42px in rem */
  height: 2.625rem; /* 42px in rem */
  border-radius: var(--radius-md);
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.25s var(--transition-smooth), opacity 0.25s var(--transition-smooth);
  
  &:hover {
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-modal-backdrop);
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    background: ${props => props.$isOpen 
      ? 'var(--overlay-medium)' 
      : 'transparent'};
    backdrop-filter: ${props => props.$isOpen ? 'blur(0.25rem)' : 'none'};
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease, visibility 0.3s ease, background 0.3s var(--transition-smooth);
  }
  
  @media (min-width: 48.0625rem) { /* Above --breakpoint-tablet */
    /* On desktop, overlay is invisible but captures clicks for close functionality */
    background: transparent;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }
`;

const ContentWrapper = styled.div<{ $sidebarOpen: boolean }>`
  display: flex;
  flex: 1;
  margin-top: clamp(4.5rem, 8vh, 5rem); /* Responsive header height */
  min-height: calc(100vh - clamp(4.5rem, 8vh, 5rem));
  overflow: visible;
  transition: transform 0.3s var(--transition-smooth), filter 0.3s var(--transition-smooth);
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  @media (min-width: 48.0625rem) { /* Above --breakpoint-tablet */
    transform: translateX(${props => props.$sidebarOpen ? '17.5rem' : '0'}); /* 280px in rem */
    filter: ${props => props.$sidebarOpen ? 'blur(0.25rem)' : 'none'};
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    transform: none;
    filter: ${props => props.$sidebarOpen ? 'blur(0.25rem)' : 'none'};
  }
`;

const Sidebar = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: clamp(4.5rem, 8vh, 5rem); /* Responsive header height */
  bottom: 0;
  width: 17.5rem; /* 280px in rem */
  max-width: 17.5rem;
  background: var(--color-surface-alt);
  backdrop-filter: blur(24px);
  border-right: 1px solid var(--color-border);
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s var(--transition-smooth), 
              background 0.3s var(--transition-smooth), 
              border-color 0.3s var(--transition-smooth);
  z-index: var(--z-modal);
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: ${props => props.$isOpen 
    ? 'var(--shadow-soft)' 
    : 'none'};
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    width: 85vw;
    max-width: 20rem; /* 320px in rem */
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    width: 90vw;
    max-width: 18.75rem; /* 300px in rem */
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--spacing-8) var(--spacing-6);
  gap: var(--spacing-8);
  overflow-y: auto;
  color: var(--color-text-primary);
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  position: relative;
  
  /* Custom scrollbar - pill shape, no arrows */
  &::-webkit-scrollbar {
    width: 0.5rem; /* 8px in rem */
    -webkit-appearance: none;
  }
  
  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: var(--spacing-1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 77, 77, 0.5);
    border-radius: 999px; /* Pill shape */
    min-height: 2.5rem; /* 40px in rem */
    transition: background 0.2s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 77, 77, 0.7);
  }
  
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 77, 77, 0.5) rgba(255, 255, 255, 0.05);
`;

const SidebarSection = styled.div<{ $isBottom?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  ${props => props.$isBottom ? `
    margin-top: auto;
    padding-top: var(--spacing-8);
    border-top: 1px solid var(--color-border);
  ` : ''}
`;

const SidebarSectionTitle = styled.h3`
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.85;
`;

const SidebarButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: clamp(var(--spacing-3), 1vw, var(--spacing-4)) var(--spacing-5);
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-size: var(--font-size-sm);
  text-align: left;
  transition: transform 0.25s var(--transition-smooth), border-color 0.25s var(--transition-smooth), box-shadow 0.25s var(--transition-smooth);
  width: 100%;

  &:hover {
    transform: translateY(-0.125rem);
    border-color: var(--color-accent);
    box-shadow: var(--shadow-soft);
  }
`;

const PageLink = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: clamp(var(--spacing-3), 1vw, var(--spacing-4)) var(--spacing-5);
  background: ${({ $isActive }) => {
    if ($isActive) {
      return `linear-gradient(135deg, 
        rgba(var(--color-accent-rgb), 0.28), 
        rgba(var(--color-accent-rgb), 0.18))`;
    }
    return 'var(--color-surface)';
  }};
  color: var(--color-text-primary);
  border: 1px solid ${({ $isActive }) => ($isActive 
    ? 'var(--color-accent)' 
    : 'var(--color-border)')};
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-size: var(--font-size-sm);
  text-align: left;
  transition: transform 0.25s var(--transition-smooth), 
              border-color 0.25s var(--transition-smooth), 
              box-shadow 0.25s var(--transition-smooth),
              background 0.25s var(--transition-smooth);
  width: 100%;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
    background: radial-gradient(circle at 20% 20%, 
      rgba(var(--color-accent-rgb), 0.4), 
      transparent 55%);
    transition: opacity 0.25s var(--transition-smooth);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-0.125rem);
    border-color: ${({ $isActive }) => ($isActive 
      ? 'var(--color-accent-light)' 
      : 'var(--color-border-strong)')};
    box-shadow: ${({ $isActive }) => ($isActive 
      ? 'var(--shadow-accent)' 
      : 'var(--shadow-soft)')};
  }
`;

const BrandInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.h1`
  font-size: clamp(var(--font-size-xl), 3vw, var(--font-size-2xl));
  font-weight: 700;
  color: white;
  margin: 0;
  letter-spacing: 0.0625rem;
`;

const BrandSubtitle = styled.p`
  font-size: var(--font-size-sm);
  color: #b0b0b0;
  margin: 0;
  font-weight: 400;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    font-size: var(--font-size-xs);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    display: none;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--spacing-2);
`;

const GreetingText = styled.div`
  display: flex;
  align-items: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: 500;
  margin-left: var(--spacing-4);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    font-size: var(--font-size-sm);
    margin-left: var(--spacing-3);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: var(--font-size-xs);
    margin-left: var(--spacing-2);
  }
`;

const MainContent = styled.main<{ $isChat?: boolean; $isPregame?: boolean; $isHome?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${props => {
    if (props.$isChat) return '0';
    if (props.$isPregame) return '0';
    if (props.$isHome) return '0';
    return `var(--spacing-8) clamp(var(--spacing-6), 4vw, var(--spacing-10))`;
  }};
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  min-height: 0;
  overflow: visible;
  background: transparent;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 64rem) { /* --breakpoint-desktop */
    padding: ${props => {
      if (props.$isChat) return '0';
      if (props.$isPregame) return '0';
      if (props.$isHome) return '0';
      return `var(--spacing-6) var(--spacing-8)`;
    }};
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: ${props => {
      if (props.$isChat) return '0';
      if (props.$isPregame) return '0';
      if (props.$isHome) return '0';
      return `var(--spacing-5)`;
    }};
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: ${props => {
      if (props.$isChat) return '0';
      if (props.$isPregame) return '0';
      if (props.$isHome) return '0';
      return `var(--spacing-4)`;
    }};
  }
`;

// Removed unused styled components to fix linting warnings

const ContentArea = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: visible;
  -webkit-overflow-scrolling: touch;
  background: transparent;
`;

interface LayoutProps {
  children: React.ReactNode;
  currentPage: 'home' | 'pregame' | 'stats' | 'livegame' | 'settings';
  onPageChange: (page: 'home' | 'pregame' | 'stats' | 'livegame' | 'settings') => void;
  onGoToOpponentReports?: () => void;
  onGoToUserReports?: () => void;
  onExportReport?: () => void;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  currentPage,
  onPageChange,
  onGoToOpponentReports,
  onGoToUserReports,
  onExportReport
}) => {
  const { getMatchupTitle, isLoading, matchupConfig, loadLeagues } = useTeam();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [loadingTeamSelector, setLoadingTeamSelector] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleOpenTeamSelector = async () => {
    setSidebarOpen(false);
    setLoadingTeamSelector(true);
    
    try {
      // Pre-fetch all necessary data before opening the modal
      // 1. Load leagues
      await loadLeagues();
      
      // 2. If user has organization, pre-fetch organization team
      if (user?.organization_id && (user?.organization_role === 'admin' || user?.organization_role === 'owner' || user?.role !== 'system_admin')) {
        try {
          await apiFetch('/api/v1/organizations/me/team');
        } catch (err) {
          console.warn('Failed to pre-fetch organization team:', err);
          // Continue anyway - TeamSelector will handle this
        }
      }
      
      // All data loaded, now open the modal
      setShowTeamSelector(true);
    } catch (err) {
      console.error('Failed to load data for team selector:', err);
      // Still open the modal - it will handle loading states internally
      setShowTeamSelector(true);
    } finally {
      setLoadingTeamSelector(false);
    }
  };

  const handleCloseTeamSelector = () => {
    setShowTeamSelector(false);
  };
  const handleLogout = async () => {
    await logout();
    setSidebarOpen(false);
  };

  const handlePageChange = (page: 'home' | 'pregame' | 'stats' | 'livegame') => {
    onPageChange(page);
    setSidebarOpen(false);
  };

  // Handle click outside sidebar to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        // Check if click is not on the hamburger menu
        const target = event.target as HTMLElement;
        const hamburgerMenu = target.closest('[aria-label="Toggle navigation menu"]');
        if (!hamburgerMenu) {
          closeSidebar();
        }
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [sidebarOpen]);

  return (
    <LayoutContainer>
      <BackgroundGlow />
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          <HamburgerMenu
            onClick={toggleSidebar}
            aria-label="Toggle navigation menu"
            type="button"
            data-track-event="nav_toggle"
            data-track-label="Sidebar Toggle"
            data-track-context="header"
          >
          <FontAwesomeIcon icon={faBars} />
        </HamburgerMenu>
          <GreetingText>
            Hello, {user?.username || user?.email?.split('@')[0] || 'User'}!
          </GreetingText>
        </div>
        
        <HeaderActions>
          {currentPage !== 'home' && (
            <HeaderLogo 
              src="/logo_red.png" 
              alt="Vermilion Logo" 
              onClick={() => handlePageChange('home')}
              aria-label="Go to home page"
              data-track-event="nav_click"
              data-track-label="Logo Home"
              data-track-context="header"
            />
          )}
          <ConfigButton
            onClick={handleOpenTeamSelector}
            aria-label="Open configuration"
            type="button"
            disabled={loadingTeamSelector}
            data-track-event="open_configuration"
            data-track-label="Header Configure"
            data-track-context="header"
          >
            <FontAwesomeIcon icon={faCog} spin={loadingTeamSelector} />
          </ConfigButton>
        </HeaderActions>
      </Header>

      <Overlay $isOpen={sidebarOpen} onClick={closeSidebar} />

      <Sidebar $isOpen={sidebarOpen} ref={sidebarRef}>
        <SidebarContent>
          <SidebarSection>
            <SidebarSectionTitle>Pages</SidebarSectionTitle>
            <PageLink
              $isActive={currentPage === 'home'}
              onClick={() => handlePageChange('home')}
              type="button"
              aria-current={currentPage === 'home' ? 'page' : undefined}
              data-track-event="nav_click"
              data-track-label="Home"
              data-track-context="sidebar"
              data-track-detail="home"
            >
              <FontAwesomeIcon icon={faHome} /> Home
            </PageLink>
            <PageLink
              $isActive={currentPage === 'pregame'}
              onClick={onGoToOpponentReports}
              type="button"
              aria-current={currentPage === 'pregame' ? 'page' : undefined}
              data-track-event="nav_click"
              data-track-label="Pre-Game Preparation"
              data-track-context="sidebar"
              data-track-detail="pregame"
            >
              <FontAwesomeIcon icon={faChartBar} /> Pre-Game Preparation
            </PageLink>
            <PageLink
              $isActive={currentPage === 'stats'}
              onClick={onGoToUserReports}
              type="button"
              aria-current={currentPage === 'stats' ? 'page' : undefined}
              data-track-event="nav_click"
              data-track-label="Stats"
              data-track-context="sidebar"
              data-track-detail="stats"
            >
              <FontAwesomeIcon icon={faChartLine} /> Stats
            </PageLink>
          </SidebarSection>

          <SidebarSection>
            <SidebarSectionTitle>Actions</SidebarSectionTitle>
            <SidebarButton
              onClick={handleOpenTeamSelector}
              type="button"
              disabled={loadingTeamSelector}
              data-track-event="open_configuration"
              data-track-label="Sidebar Configure Teams"
              data-track-context="sidebar"
            >
              <FontAwesomeIcon icon={faCog} spin={loadingTeamSelector} /> Configure Teams
            </SidebarButton>
            {/* {onExportReport && (
              <SidebarButton
                onClick={onExportReport}
                type="button"
                data-track-event="export_report"
                data-track-label="Export Report"
                data-track-context="sidebar"
              >
                <FontAwesomeIcon icon={faFileExport} /> Export Report
              </SidebarButton>
            )} */}
            <ThemeToggleButton />
          </SidebarSection>

          {!isLoading && (
            <SidebarSection>
              <SidebarSectionTitle>Current Matchup</SidebarSectionTitle>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', padding: '0.5rem 0' }}>
                {getMatchupTitle()}
              </div>
            </SidebarSection>
          )}

          <SidebarSection $isBottom>
            <SidebarButton onClick={() => {
              onPageChange('settings');
              setSidebarOpen(false);
            }} type="button">
              <FontAwesomeIcon icon={faSlidersH} /> Settings
            </SidebarButton>
            <SidebarButton onClick={handleLogout} type="button">
              Sign Out
            </SidebarButton>
          </SidebarSection>
        </SidebarContent>
      </Sidebar>
      
      <ContentWrapper $sidebarOpen={sidebarOpen}>
        <MainContent $isChat={false} $isPregame={currentPage === 'pregame'} $isHome={currentPage === 'home'}>
          <ContentArea>
            {children}
          </ContentArea>
        </MainContent>
      </ContentWrapper>

      <Modal isOpen={showTeamSelector} onClose={handleCloseTeamSelector}>
        <TeamSelector onClose={handleCloseTeamSelector} />
      </Modal>
    </LayoutContainer>
  );
}; 
